import { animate } from 'motion'

// Hero banner intro, in four beats:
//   1. On load: the red-background illustration (mono graphic + 5 icons) scales,
//      rotates and fades in, then holds.
//   2. Those icons scale/rotate/fade back out as the first slideshow photo fades
//      in and a new trio of icons (Sequence 2) scales/rotates/fades in.
//   3. The second photo fades in as Sequence 2 scales/rotates/fades out and
//      Sequence 3 fades in, shifting down into place with a slight rotation.
//   4. The third photo fades in and Sequence 3 simply rotates to a new angle —
//      it stays visible, and both it and the photo rest there for good.
//
// The whole thing is one Motion sequence: every segment below is given its own
// absolute `at` (seconds from page load), so the beats above are just labelled
// points in time (T_*) rather than a pyramid of nested setTimeouts.
document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('#hero-intro')
    if (!root) return

    const seq1 = Array.from(root.querySelectorAll('.hero-intro__seq-1'))
    const seq2 = Array.from(root.querySelectorAll('.hero-intro__seq-2'))
    const seq3 = Array.from(root.querySelectorAll('.hero-intro__seq-3'))
    const photos = Array.from(root.querySelectorAll('.hero-intro__photo'))

    const rotate = (el, attr = 'rotate') => parseFloat(el.dataset[attr] ?? '0')

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Reduced motion: settle on the static illustration and skip the photo
    // sequence entirely.
    if (reducedMotion || !photos.length) {
        seq1.forEach((el) => {
            el.style.opacity = 1
            el.style.scale = 1
            el.style.rotate = `${rotate(el)}deg`
        })
        return
    }

    // Matches the `--ease-bounce` custom property in src/styles/base/animation.css.
    const EASE_BOUNCE = [0.47, 1.64, 0.41, 0.8]

    const ENTRANCE_ROTATION_OFFSET = 40
    const SHIFT_ROTATION_OFFSET = 10
    const ICON_STAGGER = 0.088
    const ICON_DURATION = 0.77
    const HOLD_INTRO = 2.2
    const HOLD_SLIDE = 2.75
    const FADE_SLIDE = 0.99
    const SEQ3_DROP_DISTANCE = 128
    const HANDOFF_DELAY = 0.33
    const START_DELAY = 0.33

    // Beat timings, in seconds from page load.
    const T_SEQ1_OUT = START_DELAY + HOLD_INTRO
    const T_SEQ2_IN = T_SEQ1_OUT + HANDOFF_DELAY
    const T_SEQ3_TRANSITION = T_SEQ1_OUT + HOLD_SLIDE
    const T_SEQ3_IN = T_SEQ3_TRANSITION + HANDOFF_DELAY
    const T_FINAL = T_SEQ3_TRANSITION + HOLD_SLIDE

    // Scale/rotate/fade a group of icons in, staggered from `at`. Each
    // element's rest rotation comes from its `data-rotate` attribute; it
    // enters from an offset either side of that angle.
    const revealSegments = (icons, at) =>
        icons.map((el, i) => {
            const to = rotate(el)
            const from = to + (i % 2 === 0 ? ENTRANCE_ROTATION_OFFSET : -ENTRANCE_ROTATION_OFFSET)

            return [
                el,
                { opacity: [0, 1], scale: [0.5, 1], rotate: [from, to] },
                { duration: ICON_DURATION, ease: EASE_BOUNCE, at: at + i * ICON_STAGGER },
            ]
        })

    // The reverse of revealSegments — scale/rotate/fade a group of icons back
    // out along the same rotational path they entered from.
    const hideSegments = (icons, at) =>
        icons.map((el, i) => {
            const from = rotate(el)
            const to = from + (i % 2 === 0 ? ENTRANCE_ROTATION_OFFSET : -ENTRANCE_ROTATION_OFFSET)

            return [
                el,
                { opacity: [1, 0], scale: [1, 0.5], rotate: [from, to] },
                { duration: ICON_DURATION, ease: EASE_BOUNCE, at: at + i * ICON_STAGGER },
            ]
        })

    // Sequence 3's entrance: no scale — it fades in while shifting down into
    // place with only a slight rotation.
    const dropSegments = (icons, at, fromY) =>
        icons.map((el, i) => {
            const to = rotate(el)
            const from = to + (i % 2 === 0 ? SHIFT_ROTATION_OFFSET : -SHIFT_ROTATION_OFFSET)

            return [
                el,
                { opacity: [0, 1], rotate: [from, to], y: [fromY, 0] },
                { duration: ICON_DURATION, ease: EASE_BOUNCE, at: at + i * ICON_STAGGER },
            ]
        })

    // Sequence 4: Sequence 3's icons stay visible and just rotate to their
    // `data-rotate-final` angle.
    const rotateSegments = (icons, at) =>
        icons.map((el, i) => [
            el,
            { rotate: [rotate(el), rotate(el, 'rotateFinal')] },
            { duration: ICON_DURATION, ease: EASE_BOUNCE, at: at + i * ICON_STAGGER },
        ])

    const photoSegment = (photo, opacity, at) => (photo ? [[photo, { opacity }, { duration: FADE_SLIDE, at }]] : [])

    // The intro's own segments above already step through photos in their
    // rendered order. Cycle on the final segment, forever, on the same
    // hold/fade timing as the intro itself.
    const LOOP_ORDER = photos.filter(Boolean)

    const loopPhotos = () => {
        if (LOOP_ORDER.length < 2) return

        animate(
            LOOP_ORDER.flatMap((to, i) => {
                const from = LOOP_ORDER[(i + LOOP_ORDER.length - 1) % LOOP_ORDER.length]
                const at = (i + 1) * (HOLD_SLIDE + FADE_SLIDE)

                return [...photoSegment(from, [1, 0], at), ...photoSegment(to, [0, 1], at)]
            }),
        ).then(loopPhotos)
    }

    animate([
        // 1. Entrance.
        ...revealSegments(seq1, START_DELAY),

        // 2. Sequence 1 out, first photo in, Sequence 2 in shortly after.
        ...hideSegments(seq1, T_SEQ1_OUT),
        ...photoSegment(photos[0], [0, 1], T_SEQ1_OUT),
        ...revealSegments(seq2, T_SEQ2_IN),

        // 3. Second photo in, Sequence 2 out, Sequence 3 in shortly after.
        ...hideSegments(seq2, T_SEQ3_TRANSITION),
        ...photoSegment(photos[1], [0, 1], T_SEQ3_TRANSITION),
        ...photoSegment(photos[0], [1, 0], T_SEQ3_TRANSITION),
        ...dropSegments(seq3, T_SEQ3_IN, -SEQ3_DROP_DISTANCE),

        // 4. Third photo in, Sequence 3 just rotates in place.
        ...photoSegment(photos[2], [0, 1], T_FINAL),
        ...photoSegment(photos[1], [1, 0], T_FINAL),
        ...rotateSegments(seq3, T_FINAL),
    ]).then(loopPhotos)
})
